import { useEffect, useState } from 'react';
import { DISCIPLINE_PALETTE, type Discipline } from '@obraplan/core/domain/structure/discipline';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export interface DisciplineFormSubmit {
  readonly name: string;
  readonly code: string | undefined;
  readonly color: string;
}

/**
 * Cadastrar ou corrigir uma disciplina do catálogo global.
 *
 * O mesmo formulário serve aos dois casos porque os campos são exatamente os mesmos — o
 * que muda é o ponto de partida. Renomear é operação legítima: a disciplina continua
 * sendo a mesma para as frentes que já a usam, só passa a se chamar certo.
 *
 * A cor é apoio visual na árvore, nunca o único indicador: o nome está sempre escrito ao
 * lado.
 */
export function DisciplineFormDialog({
  open,
  onOpenChange,
  discipline,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente = nova disciplina. */
  discipline: Discipline | undefined;
  onSubmit: (input: DisciplineFormSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const isEditing = discipline !== undefined;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [color, setColor] = useState(DISCIPLINE_PALETTE[0]!);

  // O formulário é remontado a cada abertura para não herdar o que foi digitado da vez
  // anterior — nem a disciplina anterior, quando a tela abre uma depois da outra.
  useEffect(() => {
    if (!open) return;
    setName(discipline?.name ?? '');
    setCode(discipline?.code ?? '');
    setColor(discipline?.color ?? DISCIPLINE_PALETTE[0]!);
  }, [open, discipline]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar disciplina' : 'Nova disciplina'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'A mudança vale para todas as obras. As frentes já cadastradas continuam apontando para esta mesma disciplina.'
              : 'Fica disponível em todas as obras, agora e nas que vierem depois.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              name,
              code: code.trim() === '' ? undefined : code.trim(),
              color,
            });
          }}
        >
          <Field
            label="Nome da disciplina"
            htmlFor="discipline-form-name"
            required
            error={errorMessage}
          >
            <Input
              id="discipline-form-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Impermeabilização"
            />
          </Field>

          <Field
            label="Código"
            htmlFor="discipline-form-code"
            hint="Opcional — a sigla usada nas planilhas da equipe."
          >
            <Input
              id="discipline-form-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">Cor na árvore</legend>
            <div className="flex flex-wrap gap-2">
              {DISCIPLINE_PALETTE.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-label={`Cor ${option}`}
                  aria-pressed={color === option}
                  onClick={() => setColor(option)}
                  className={cn(
                    'size-8 rounded-full border-2 transition-transform',
                    color === option
                      ? 'border-(--color-content) scale-110'
                      : 'border-transparent hover:scale-105',
                  )}
                  style={{ backgroundColor: option }}
                />
              ))}
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || name.trim() === ''}>
              {isPending ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar disciplina'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
